/**
 * Phase 125.30 — Full-Width Countdown-Slider mit Hintergrundbild.
 *
 * Ziel: Reservierungsbeginn / wichtige Termine ankündigen, mit Live-Countdown.
 * Kein CTA-Button (Funktion existiert (noch) nicht) — nur Titel, Untertitel,
 * Countdown und ein weich abgedunkeltes Hintergrundbild.
 *
 * Config aus `settings.home.countdown_slider`:
 *   { enabled, title, subtitle, target_iso, background_image_url }
 *
 * Wellen oben & unten in der Wrapper-Farbe für sauberen Übergang.
 */
import React, { useEffect, useState } from 'react';
import { CalendarClock } from 'lucide-react';
import { isVideoUrl } from './ImageUpload';
import { resolveImageUrl } from '../context/SettingsContext';

const pad = (n) => String(n).padStart(2, '0');

const useCountdown = (targetIso) => {
  const [remaining, setRemaining] = useState(() => {
    if (!targetIso) return null;
    return Math.max(0, new Date(targetIso).getTime() - Date.now());
  });
  useEffect(() => {
    if (!targetIso) return undefined;
    const t = setInterval(() => {
      setRemaining(Math.max(0, new Date(targetIso).getTime() - Date.now()));
    }, 1000);
    return () => clearInterval(t);
  }, [targetIso]);
  if (remaining == null) return null;
  const days = Math.floor(remaining / (24 * 3600 * 1000));
  const hours = Math.floor((remaining % (24 * 3600 * 1000)) / (3600 * 1000));
  const mins = Math.floor((remaining % (3600 * 1000)) / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);
  return { days, hours, mins, secs, isPast: remaining === 0 };
};

// Platzhalter: bunte Luftballons / Party-Stimmung (Unsplash, CDN)
const DEFAULT_BG =
  'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=2000&q=70';

const HomeCountdownSlider = ({ config, topBg, bottomBg, wrapperBg }) => {
  const cfg = config || {};
  const defaultTarget = React.useMemo(() => {
    const nextYear = new Date().getFullYear() + 1;
    return `${nextYear}-01-01T00:00:00+01:00`;
  }, []);
  const targetIso = cfg.target_iso || defaultTarget;
  const countdown = useCountdown(targetIso);
  if (cfg.enabled === false) return null;
  const title = cfg.title || 'Reservierungsbeginn für die Einschulung 2027';
  const subtitle = cfg.subtitle || 'Ab 1. Januar 0 Uhr möglich';
  const bgUrl = cfg.background_image_url || cfg.image_url || DEFAULT_BG;
  // Overlay-Konfiguration (CMS): Farbe (#RRGGBB) + Deckkraft (0-100).
  // Fallback: dunkles Sky-Blau mit ~64% Deckkraft, damit weißer Text lesbar bleibt.
  const overlayColor = (cfg.overlay_color || '#032c4f').trim();
  const overlayOpacityRaw = cfg.overlay_opacity;
  const overlayOpacity = (typeof overlayOpacityRaw === 'number' && overlayOpacityRaw >= 0 && overlayOpacityRaw <= 100)
    ? overlayOpacityRaw / 100
    : 0.64;
  const bgIsVideo = isVideoUrl(bgUrl);
  const isPast = countdown?.isPast;
  const topFill = topBg === null ? null : (topBg || wrapperBg || '#ffffff');
  const bottomFill = bottomBg === null ? null : (bottomBg || wrapperBg || '#ffffff');

  return (
    <section
      className="relative overflow-hidden py-10 md:py-14"
      data-testid="home-countdown-slider"
      style={bgIsVideo ? { backgroundColor: '#032c4f' } : {
        backgroundImage: `url("${bgUrl}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {bgIsVideo && (
        <video
          src={resolveImageUrl(bgUrl)}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay muted loop playsInline preload="metadata"
          aria-hidden="true"
          data-testid="cd-bg-video"
        />
      )}
      {/* Dunkler Farbverlauf für Lesbarkeit — Farbe & Deckkraft im Admin einstellbar */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{ backgroundColor: overlayColor, opacity: overlayOpacity }}
      />

      {/* Top wave */}
      {topFill && (
        <svg
          className="absolute left-0 w-full h-8 md:h-12 block pointer-events-none z-10"
          viewBox="0 0 1440 100"
          preserveAspectRatio="none"
          style={{ display: 'block', transform: 'rotate(180deg)', top: '-2px' }}
          aria-hidden="true"
        >
          <path
            fill={topFill}
            d="M0,50 C 240,50 320,25 480,25 C 640,25 720,50 960,50 C 1200,50 1280,25 1440,25 L1440,100 L0,100 Z"
          />
        </svg>
      )}
      {/* Bottom wave */}
      {bottomFill && (
        <svg
          className="absolute left-0 w-full h-8 md:h-12 block pointer-events-none z-10"
          viewBox="0 0 1440 100"
          preserveAspectRatio="none"
          style={{ display: 'block', bottom: '-2px' }}
          aria-hidden="true"
        >
          <path
            fill={bottomFill}
            d="M0,50 C 240,50 320,25 480,25 C 640,25 720,50 960,50 C 1200,50 1280,25 1440,25 L1440,100 L0,100 Z"
          />
        </svg>
      )}

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 z-20">
        <div className="flex flex-col items-center text-center text-white">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-400 text-sky-900 text-[11px] font-extrabold uppercase tracking-wider mb-3 shadow">
            <CalendarClock className="h-3.5 w-3.5" />
            Vormerken
          </div>
          <h3
            className="text-xl md:text-3xl font-bold leading-tight drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)] max-w-3xl"
            data-testid="countdown-title"
          >
            {title}
          </h3>
          <p
            className="text-sky-50 text-xs md:text-sm mt-2 drop-shadow-[0_1px_3px_rgba(0,0,0,0.55)]"
            data-testid="countdown-subtitle"
          >
            {subtitle}
          </p>

          {countdown && !isPast && (
            <div
              className="mt-5 md:mt-6 flex flex-wrap justify-center items-center gap-2 md:gap-3"
              data-testid="countdown-timer"
            >
              {[
                { v: countdown.days, l: 'Tage' },
                { v: countdown.hours, l: 'Std' },
                { v: countdown.mins, l: 'Min' },
                { v: countdown.secs, l: 'Sek' },
              ].map((x) => (
                <div
                  key={x.l}
                  className="rounded-2xl bg-white/12 backdrop-blur-md border border-white/25 px-3 py-2 md:px-4 md:py-2.5 min-w-[62px] md:min-w-[80px] text-center shadow-lg"
                >
                  <div className="text-2xl md:text-4xl font-black text-white tabular-nums leading-none drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                    {pad(x.v)}
                  </div>
                  <div className="text-[9px] md:text-[11px] font-bold uppercase tracking-wider text-sky-100/90 mt-1">
                    {x.l}
                  </div>
                </div>
              ))}
            </div>
          )}

          {isPast && (
            <div
              className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/30 border border-emerald-300/50 text-white text-sm font-bold shadow"
              data-testid="countdown-open"
            >
              ✨ Reservierungen jetzt möglich!
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default HomeCountdownSlider;
